/**
 * The concepts registry.
 *
 * A concept is one idea from the science and philosophy layer: one master
 * lesson rendered at two depths (depth.js). The Platform read is its core;
 * the Certification lesson is the whole thing, deeper sections and
 * activities included, and stands on its own.
 *
 * Where they live:
 *
 *   The Platform version is the Learning Lab's Concepts lane. It replaces the
 *   foundations flashcards there, which never reached a coach: the Lab has
 *   been closed the whole time they existed. A lane is the right home because
 *   the Lab is the surface designed to be opened daily, and a concept is
 *   exactly the kind of thing you want a coach meeting one of, regularly,
 *   rather than finding all of at once in a list.
 *
 *   The Certification version lives in the cert.
 *
 * Read state is a user_data key rather than lab_seen. lab_seen is keyed by
 * item type and id against content rows; a concept is neither, and keeping it
 * here means the Concepts lane needed no migration to ship. It lives in
 * conceptReads.js so this file stays free of the Supabase client.
 */
import { AFFORDANCES } from "./affordancesModule";
import { REPETITION } from "./repetitionModule";
import { VARIABILITY } from "./variabilityModule";
import { DIFFERENTIAL_LEARNING } from "./differentialLearningModule";
import { DEGREES_OF_FREEDOM } from "./degreesOfFreedomModule";
import { CHALLENGE_POINT } from "./challengePointModule";
import { ATTRACTORS } from "./attractorsModule";
import { TYPES_OF_CONSTRAINTS } from "./typesOfConstraintsModule";
import { REPRESENTATIVE_LEARNING_DESIGN } from "./representativeLearningDesignModule";
import { PERCEPTION_ACTION_COUPLING } from "./perceptionActionCouplingModule";
import { FEEL_VS_IQ } from "./feelVsIqModule";
import { SHOOTING_CONSISTENCY } from "./shootingConsistencyModule";
import { platformView, certView } from "./depth";

/* Each concept is one master lesson; depth.js renders its Platform read
   and its Certification lesson from it. */
export const CONCEPT_MASTERS = [AFFORDANCES, REPETITION, VARIABILITY, DIFFERENTIAL_LEARNING, DEGREES_OF_FREEDOM, CHALLENGE_POINT, ATTRACTORS, TYPES_OF_CONSTRAINTS, REPRESENTATIVE_LEARNING_DESIGN, PERCEPTION_ACTION_COUPLING, FEEL_VS_IQ, SHOOTING_CONSISTENCY];

const entry = (master, meta) => {
  const module = platformView(master);
  return { key: module.key, title: master.title, minutes: parseInt(module.duration, 10), ...meta,
    module, certKey: master.cert.key, cert: certView(master) };
};

/* Ordered. The lane serves them in this order, unread first, so a coach
   works through the curriculum rather than meeting it at random. */
export const CONCEPTS = [
  // `added`: when it shipped. Drives the NEW CONCEPT marks until opened.
  entry(AFFORDANCES, { blurb: "What Does the Player Actually See?", added: "2026-09-27" }),
  entry(REPETITION, { blurb: "How to Get More Reps Without Just Reciting the Same Solution", added: "2026-10-07" }),
  entry(VARIABILITY, { blurb: "Stable enough to perform. Adaptable enough to solve.", added: "2026-10-08" }),
  entry(DIFFERENTIAL_LEARNING, { blurb: "Push the Borders. Reorganize the Skill.", added: "2026-10-09" }),
  entry(DEGREES_OF_FREEDOM, { blurb: "Freedom Is Only Useful If You Can Organize It.", added: "2026-10-09" }),
  entry(CHALLENGE_POINT, { blurb: "Hard Is Not the Goal. Productive Struggle Is.", added: "2026-10-09" }),
  entry(ATTRACTORS, { blurb: "Stable Enough to Trust. Flexible Enough to Adapt.", added: "2026-10-12" }),
  // heldForDrop (Coleman, 2026-10-14): the newest three are held back on the
  // Platform and released one a week, each by the Wednesday Drop that
  // features it. Their Certification lessons are not held.
  entry(TYPES_OF_CONSTRAINTS, { blurb: "Design the Problem. Let the Athlete Solve It.", added: "2026-10-12", heldForDrop: true }),
  entry(REPRESENTATIVE_LEARNING_DESIGN, { blurb: "How Much of the Game Do We Really Need?", added: "2026-10-13", heldForDrop: true }),
  entry(PERCEPTION_ACTION_COUPLING, { blurb: "Why Doing a Skill Isn't the Same as Using It", added: "2026-10-13", heldForDrop: true }),
  entry(FEEL_VS_IQ, { blurb: "Two Sides of Basketball Intelligence, and How to Develop Both", added: "2026-10-15", heldForDrop: true }),
  // 2026-11-03. Released immediately (not heldForDrop). No card blurb, by
  // his call; the card shows the title alone.
  entry(SHOOTING_CONSISTENCY, { blurb: "", added: "2026-11-03" }),
];

export const CONCEPT_MODULES = CONCEPTS.reduce((m, c) => { m[c.key] = c.module; return m; }, {});
export const CONCEPT_CERT_MODULES = CONCEPTS.reduce((m, c) => { m[c.certKey] = c.cert; return m; }, {});
export const ALL_CONCEPT_MODULES = { ...CONCEPT_MODULES, ...CONCEPT_CERT_MODULES };

export const CONCEPT_BY_KEY = CONCEPTS.reduce((m, c) => { m[c.key] = c; return m; }, {});

/**
 * The concepts a coach can see on the Platform. A concept held for a drop
 * appears once a published drop has featured it (`released`: key -> the
 * drop's publish time, from conceptReads.loadReleasedConcepts), and counts
 * as added on that day, so the NEW marks run from its release rather than
 * from when it was built. Unknown or still loading means held.
 */
export function visibleConcepts(released = {}) {
  const rel = released || {};
  return CONCEPTS
    .filter(c => !c.heldForDrop || rel[c.key])
    .map(c => (c.heldForDrop ? { ...c, added: String(rel[c.key]).slice(0, 10) } : c));
}

/**
 * What the Concepts lane serves: unread first, in curriculum order, then the
 * ones already read so the lane is never empty. A coach who has read all of
 * them should get the chance to reread rather than a locked door, which is
 * the mistake the foundations lane made.
 *
 * Pure. Read state is loaded by conceptReads.js and passed in, so this file
 * pulls in no Supabase client and the tests can import the registry without
 * standing one up.
 */
export function conceptSet(reads = {}, limit = 4, released = {}) {
  const r = reads || {};
  const list = visibleConcepts(released);
  const unread = list.filter(c => !r[c.key]);
  const read = list.filter(c => r[c.key]);
  return [...unread, ...read].slice(0, limit)
    .map(c => ({ ...c, read: !!r[c.key] }));
}
